import { MotionConfig, fadeUp, motion } from '@/lib/motion';
import {
  ArrowRight,
  Code2,
  Palette,
  PhoneCall,
  Quote,
  ShieldCheck,
  Sparkles,
  Zap,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import { useBrand } from '@/brand/brand-provider';
import { ChatMock } from '@/components/marketing/chat-mock';
import { EmbedSnippet } from '@/components/marketing/embed-snippet';
import { Button } from '@/components/ui/button';
import { Seo } from '@/lib/seo';

const FEATURES = [
  {
    icon: Sparkles,
    title: 'Same knowledge base as voice',
    desc: 'The widget answers from the exact knowledge base your phone agent uses. Train once; answers stay consistent across both channels.',
  },
  {
    icon: Zap,
    title: 'Streams answers with citations',
    desc: 'Responses stream token-by-token and cite the source doc they came from, so visitors trust the answer and you can see why it was given.',
  },
  {
    icon: Code2,
    title: 'One script tag to install',
    desc: 'Drop a single <script> tag before </body>. No framework, no build step — set theme and position with data attributes.',
  },
  {
    icon: ShieldCheck,
    title: 'Abuse-safe by default',
    desc: 'Each widget key is locked to your origin, rate-limited, and capped by a daily AI-spend ceiling. Predictable cost, no runaway bills.',
  },
  {
    icon: Palette,
    title: 'Matches your brand',
    desc: 'Light or dark theme, your accent, your position. The widget looks like part of your site, not a bolt-on.',
  },
  {
    icon: Quote,
    title: 'Captures the conversation',
    desc: 'Every chat is saved with an AI summary in your dashboard, so you can follow up and see what visitors actually ask.',
  },
];

const FAQS = [
  {
    q: 'How do I install the chat widget?',
    a: 'Paste one <script> tag before your closing </body>. That’s the whole install — no framework or build step. Theme and position are set with data attributes.',
  },
  {
    q: 'Does the chatbot use the same knowledge as my phone agent?',
    a: 'Yes — train once. The widget and the phone agent answer from the same knowledge base, so customers get identical answers on either channel.',
  },
  {
    q: 'What stops bots from running up my bill?',
    a: 'Each widget key is locked to your origin, rate-limited, and capped by a daily AI-spend ceiling per plan. Cost stays predictable by default.',
  },
  {
    q: 'Can I match it to my brand?',
    a: 'Yes — choose light or dark theme, your accent color, and the launcher position, all via the embed snippet’s data attributes.',
  },
];

export function ChatPage(): JSX.Element {
  const brand = useBrand();
  return (
    <MotionConfig reducedMotion="user">
      <Seo
        title={`AI Chat Widget — ${brand.displayName}`}
        description="An embeddable AI chat widget that answers your website visitors instantly from the same knowledge base as your phone agent. Install with one script tag; origin-locked and spend-capped."
        path="/chat"
        image="/og-chat.png"
      />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute left-1/2 top-0 h-[500px] w-[900px] -translate-x-1/2 -translate-y-1/3 rounded-full bg-gradient-to-br from-emerald-500/20 via-emerald-500/5 to-transparent blur-3xl" />
        </div>
        <div className="container grid grid-cols-1 items-center gap-12 py-14 sm:py-20 lg:grid-cols-2">
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <span className="inline-flex items-center gap-2 rounded-full bg-emerald-500/10 px-3 py-1 text-xs font-semibold text-emerald-600 dark:text-emerald-400">
              AI Chat Widget
            </span>
            <h1 className="mt-5 text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
              An AI that answers your website visitors — in your words, instantly.
            </h1>
            <p className="mt-5 max-w-xl text-lg text-muted-foreground">
              Embed it in one script tag. It streams answers from the same knowledge
              base as your phone agent, cites its sources, and stays locked to your
              origin and your budget.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Button asChild size="lg">
                <Link to="/signup">
                  Get started <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link to="/pricing">See pricing</Link>
              </Button>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            <ChatMock framed />
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="border-y border-border bg-muted/20 py-14 sm:py-20">
        <div className="container">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
              The chat widget, the way it should work.
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ delay: (i % 3) * 0.1 }}
                className="rounded-2xl border border-border bg-card p-6"
              >
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                  <f.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 font-semibold">{f.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Embed snippet */}
      <section className="container py-14 sm:py-20">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
          <div>
            <span className="text-xs font-semibold uppercase tracking-widest text-emerald-600 dark:text-emerald-400">
              Install in 60 seconds
            </span>
            <h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">
              One tag. That&apos;s the whole install.
            </h2>
            <p className="mt-4 text-muted-foreground">
              Paste the snippet before your closing <code className="rounded bg-muted px-1 py-0.5 text-sm">&lt;/body&gt;</code>
              tag. Swap in your widget key, pick a theme and position, and you&apos;re live.
              No framework, no build step, no backend to run.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-muted-foreground">
              <li>• <code className="text-foreground">data-chat-key</code> — your origin-locked widget key</li>
              <li>• <code className="text-foreground">data-theme</code> — light or dark</li>
              <li>• <code className="text-foreground">data-position</code> — where the launcher sits</li>
            </ul>
          </div>
          <EmbedSnippet />
        </div>
      </section>

      {/* Cross-sell to voice */}
      <section className="border-y border-border bg-muted/20 py-14 sm:py-20">
        <div className="container mx-auto flex max-w-3xl flex-col items-center gap-6 rounded-3xl border border-border bg-gradient-to-br from-[var(--brand-accent)]/5 to-transparent p-10 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[var(--brand-accent)]/10 text-[var(--brand-accent)]">
            <PhoneCall className="h-6 w-6" />
          </span>
          <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
            Same brain as your phone agent
          </h2>
          <p className="max-w-xl text-muted-foreground">
            Every plan also includes the AI voice agent — answering inbound calls from
            the very same knowledge base. One brain, every channel.
          </p>
          <Button asChild variant="outline">
            <Link to="/voice">
              Explore the voice agent <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </section>

      {/* FAQ */}
      <section className="container py-14 sm:py-20">
        <div className="mx-auto max-w-2xl">
          <h2 className="text-center text-3xl font-bold tracking-tight sm:text-4xl">
            Chat widget FAQ
          </h2>
          <div className="mt-10 space-y-4">
            {FAQS.map((f) => (
              <div key={f.q} className="rounded-2xl border border-border bg-card p-5">
                <h3 className="font-medium">{f.q}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container pb-20 sm:pb-28">
        <div className="rounded-3xl border border-border bg-gradient-to-br from-[var(--brand-accent)] to-fuchsia-600 px-6 py-14 text-center text-white sm:px-12">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
            Never miss a website visitor again.
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-white/80">
            From $39/month — chat and voice included on one knowledge base.
          </p>
          <div className="mt-8">
            <Button asChild size="lg" className="bg-white text-emerald-700 hover:bg-white/90">
              <Link to="/signup">
                Get started <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </MotionConfig>
  );
}
